import { audioManager } from './audioManager';
import type { FloorId } from '@/data/floors';

/**
 * Ambient-loop-system for rom-spesifikke bakgrunns-lyder.
 *
 * - Hver loop er en HTMLAudioElement med `loop=true`
 * - Volum-defaults er meget lave (0.05–0.10) — ambient skal være subtilt
 * - Bytte mellom rom utfører en kort crossfade (250ms) for å unngå kutt
 * - Respekterer `audioManager.erAv` og kan slås av separat via `slaaAv()`
 * - Feiler stille hvis fil mangler eller browser-policy blokkerer
 */

export type AmbientNavn = 'redaksjon' | 'marked' | 'trykkeri' | 'takterrasse';

const AMBIENT_PATHS: Record<AmbientNavn, string> = {
  redaksjon: 'assets/audio/ambient/redaksjon.mp3',
  marked: 'assets/audio/ambient/marked.mp3',
  trykkeri: 'assets/audio/ambient/trykkeri.mp3',
  takterrasse: 'assets/audio/ambient/takterrasse.mp3',
};

const VOLUM_PER_NAVN: Record<AmbientNavn, number> = {
  redaksjon: 0.08, // tastatur-klakking
  marked: 0.06, // telefon-ringing/kontorlyd
  trykkeri: 0.1, // presse-rytme
  takterrasse: 0.05, // vind + svake fugler
};

/**
 * Mapping fra spillets floor-IDs til ambient-loop. Floors som ikke
 * mapper får ingen ambient (stille).
 */
const FLOOR_TIL_AMBIENT: Partial<Record<FloorId, AmbientNavn>> = {
  redaksjon: 'redaksjon',
  marked: 'marked',
  kjeller: 'trykkeri', // kjeller = trykkeriet i bygget
  tak: 'takterrasse', // tak = takterrassen
};

const CROSSFADE_MS = 250;

export class AmbientAudio {
  private cache: Map<AmbientNavn, HTMLAudioElement> = new Map();
  private mangler: Set<AmbientNavn> = new Set();
  private naavaerende?: AmbientNavn;
  private aktivertAvBruker = false;
  private aktiv = true;

  /**
   * Bytter ambient til den som passer for gitt floor. Kalles fra
   * `ForlagshusetScene.oppdaterAmbient` ved etasje-skift.
   */
  byttFloor(id: FloorId): void {
    const navn = FLOOR_TIL_AMBIENT[id];
    this.bytt(navn);
  }

  /**
   * Bytter eksplisitt til en navngitt loop (eller stopper hvis navn=undefined).
   */
  bytt(navn?: AmbientNavn): void {
    if (navn === this.naavaerende) {
      // Samme loop — bare sørg for at den spiller hvis aktivert.
      this.oppdaterAvspilling();
      return;
    }

    // Fade ut nåværende
    if (this.naavaerende) {
      const gammel = this.cache.get(this.naavaerende);
      if (gammel) this.fadeUt(gammel);
    }

    this.naavaerende = navn;
    this.oppdaterAvspilling();
  }

  /**
   * Markerer at brukeren har interagert (typisk på første tap),
   * slik at vi kan starte avspilling uten å bli blokkert av autoplay-policy.
   */
  markerBrukerInteraksjon(): void {
    if (this.aktivertAvBruker) return;
    this.aktivertAvBruker = true;
    this.oppdaterAvspilling();
  }

  slaaAv(): void {
    this.aktiv = false;
    for (const audio of this.cache.values()) {
      try {
        audio.pause();
      } catch {
        // ignore
      }
    }
  }

  slaaPaa(): void {
    this.aktiv = true;
    this.oppdaterAvspilling();
  }

  /**
   * Stopper alt og rydder. Brukes ved scene-shutdown.
   */
  stopp(): void {
    for (const audio of this.cache.values()) {
      try {
        audio.pause();
        audio.removeAttribute('src');
        audio.load();
      } catch {
        // ignore
      }
    }
    this.cache.clear();
    this.naavaerende = undefined;
  }

  private oppdaterAvspilling(): void {
    if (!this.naavaerende) return;
    if (!this.aktiv) return;
    if (audioManager.erAv) return;
    if (!this.aktivertAvBruker) return;
    if (typeof Audio === 'undefined') return;
    if (this.mangler.has(this.naavaerende)) return;

    const audio = this.last(this.naavaerende);
    if (!audio) return;

    try {
      if (audio.paused) {
        const result = audio.play();
        if (result && typeof result.catch === 'function') {
          result.catch(() => {
            // browser-policy eller manglende fil — feil stille
          });
        }
      }
      this.fadeInn(audio);
    } catch {
      // ignore
    }
  }

  private last(navn: AmbientNavn): HTMLAudioElement | undefined {
    const eksisterende = this.cache.get(navn);
    if (eksisterende) return eksisterende;
    if (this.mangler.has(navn)) return undefined;
    try {
      const audio = new Audio(AMBIENT_PATHS[navn]);
      audio.preload = 'auto';
      audio.loop = true;
      audio.volume = 0; // start på 0, fade inn
      audio.addEventListener('error', () => {
        this.mangler.add(navn);
        this.cache.delete(navn);
      });
      this.cache.set(navn, audio);
      return audio;
    } catch {
      this.mangler.add(navn);
      return undefined;
    }
  }

  private fadeInn(audio: HTMLAudioElement): void {
    const navn = this.naavaerende;
    if (!navn) return;
    const maal = VOLUM_PER_NAVN[navn];
    this.tweenVolum(audio, maal, CROSSFADE_MS);
  }

  private fadeUt(audio: HTMLAudioElement): void {
    this.tweenVolum(audio, 0, CROSSFADE_MS, () => {
      try {
        audio.pause();
      } catch {
        // ignore
      }
    });
  }

  private tweenVolum(
    audio: HTMLAudioElement,
    maal: number,
    varighetMs: number,
    onComplete?: () => void,
  ): void {
    if (typeof window === 'undefined') {
      audio.volume = maal;
      onComplete?.();
      return;
    }
    const start = audio.volume;
    const startTid = Date.now();
    const step = () => {
      const t = Math.min(1, (Date.now() - startTid) / varighetMs);
      try {
        audio.volume = start + (maal - start) * t;
      } catch {
        // ignore
      }
      if (t < 1) {
        window.requestAnimationFrame(step);
      } else {
        onComplete?.();
      }
    };
    window.requestAnimationFrame(step);
  }
}

export const ambientAudio = new AmbientAudio();
